// 播客相关类型定义

// 播客信息
export interface PodcastInfo {
  id: number;
  title: string;
  summary_id: number;
  status: number;
  status_text?: string;
  audio_url?: string;
  duration?: number;
  speaker_type: string;
  action: number;
  created_at: string;
  completed_at?: string;
  expired_at?: string;
  error_msg?: string;
}

// 管理员播客信息（包含更多字段）
export interface AdminPodcastInfo extends PodcastInfo {
  open_id: number;
  input_text?: string;
  input_url?: string;
  cos_key?: string;
  token_used?: number;
}

// 播客列表响应
export interface PodcastListResponse {
  list: PodcastInfo[];
  total: number;
}

// 管理员播客列表响应
export interface AdminPodcastListResponse {
  list: AdminPodcastInfo[];
  total: number;
}

// 播客配置
export interface PodcastConfig {
  speaker_type: string; // "mizai" | "liufei"
  audio_format: string;
  use_head_music: boolean;
  use_tail_music: boolean;
  speech_rate: number;
  language: string;
  podcast_type?: string;
  custom_host_voice?: string;
  custom_cohost_voice?: string;
  doubao_app_id?: string;
  doubao_resource_id?: string;
  has_doubao_key?: boolean;
  specified_mode?: boolean;
}

// 更新播客配置请求
export interface UpdatePodcastConfigRequest {
  speaker_type: string;
  audio_format: string;
  use_head_music: boolean;
  use_tail_music: boolean;
  speech_rate: number;
  language: string;
}

// 删除播客请求
export interface DeletePodcastRequest {
  podcast_id: number;
}

// 播客轮次详情
export interface PodcastRoundDetail {
  round_id: number;
  speaker: string;
  text: string;
  duration: number;
  timestamp: number;
}

// 下载播客响应
export interface DownloadPodcastResponse {
  podcast_id: number;
  title: string;
  audio_data: string; // Base64编码
  audio_format: string;
  duration: number;
  created_at: number;
  rounds: PodcastRoundDetail[];
}

// 播客统计
export interface PodcastStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

// 播客状态枚举
export enum PodcastStatus {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
}

// 发音人类型
export const SPEAKER_TYPES = {
  mizai: "黑猫侦探社咪仔",
  liufei: "刘飞和潇磊",
} as const;

export type SpeakerType = keyof typeof SPEAKER_TYPES;

export const normalizePodcastConfig = (
  config?: Partial<PodcastConfig> | null
): PodcastConfig => ({
  speaker_type: config?.speaker_type || "mizai",
  audio_format: config?.audio_format || "mp3",
  use_head_music: config?.use_head_music ?? true,
  use_tail_music: config?.use_tail_music ?? false,
  speech_rate: typeof config?.speech_rate === "number" ? config.speech_rate : 0,
  language: config?.language || "zh-CN",
  podcast_type: config?.podcast_type || "builtin",
  has_doubao_key: config?.has_doubao_key ?? true,
  specified_mode: config?.specified_mode ?? true,
  doubao_app_id: config?.doubao_app_id,
  doubao_resource_id: config?.doubao_resource_id,
  custom_host_voice: config?.custom_host_voice,
  custom_cohost_voice: config?.custom_cohost_voice,
});

const getSummaryIdFromSwitchKey = (key: string) => {
  const parts = key.split(":");
  const summaryId = Number(parts[parts.length - 1]);
  return Number.isFinite(summaryId) ? summaryId : null;
};

export const extractSummarySwitchMap = (data: unknown) => {
  const result = new Map<number, boolean>();

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return result;
  }

  Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
    const summaryId = getSummaryIdFromSwitchKey(key);
    if (summaryId !== null) {
      result.set(summaryId, value === true);
    }
  });

  return result;
};

export const getSummarySwitchStatus = (data: unknown, summaryId: number) =>
  extractSummarySwitchMap(data).get(summaryId) || false;
